import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '../data/catalog';
import { useAccount } from '../context/AccountContext';
import AuthModal from './AuthModal';
import PushOptIn from './PushOptIn';
import { trackActivationEvent } from '../lib/analyticsTracking';
import styles from '../styles/AlertBuilder.module.css';

const storageKey = 'esganga-alerts';

const AlertBuilder = () => {
    const { user } = useAccount();
    const prefill = useMemo(() => getAlertPrefill(), []);
    const [keyword, setKeyword] = useState(prefill.keyword);
    const [category, setCategory] = useState(prefill.category);
    const [maxPrice, setMaxPrice] = useState(prefill.maxPrice);
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [matches, setMatches] = useState(null);
    const [authOpen, setAuthOpen] = useState(false);
    const [accountPrompt, setAccountPrompt] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const cleanKeyword = keyword.trim();

        if (!cleanKeyword) {
            setStatus('Escribe una marca, producto o tienda para crear la alerta.');
            return;
        }

        const alert = {
            id: Date.now(),
            keyword: cleanKeyword,
            category,
            maxPrice: maxPrice ? Number(maxPrice) : null,
            email: email.trim() || null,
            createdAt: new Date().toISOString()
        };

        setIsSaving(true);
        setMatches(null);
        setAccountPrompt(false);

        try {
            const response = await fetch('/api/alerts', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(alert)
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (data.code === 'ANON_ALERT_LIMIT') {
                    setStatus(data.error);
                    setAuthOpen(true);
                    return;
                }
                throw new Error('Alerts API unavailable');
            }

            saveLocalAlert(data.alert || alert);
            trackActivationEvent('alert-created');
            if (!user) setAccountPrompt(true);
            const currentMatches = await fetchCurrentMatches(alert);
            setMatches(currentMatches);
            setStatus(buildStatus(cleanKeyword, currentMatches, false, Boolean(user)));
        } catch (error) {
            saveLocalAlert(alert);
            setStatus(buildStatus(cleanKeyword, null, true, Boolean(user)));
        } finally {
            setKeyword('');
            setMaxPrice('');
            setEmail('');
            setIsSaving(false);
        }
    };

    return (
        <form className={styles.alertBuilder} onSubmit={handleSubmit}>
            <div className={styles.alertHead}>
                <span>Alerta de precio</span>
                <strong>Avisame cuando encuentre algo parecido</strong>
            </div>

            {user ? (
                <>
                    <p className={styles.accountHint}>
                        Se guardara en tu cuenta ({user.email}) y podras verla en <Link to="/mis-alertas">Mis alertas</Link>.
                    </p>
                    <PushOptIn user={user} />
                </>
            ) : (
                <div className={styles.accountCallout}>
                    <span className={styles.accountCalloutIcon} aria-hidden="true">🔐</span>
                    <p>
                        Sin iniciar sesion la alerta solo queda en este navegador.{' '}
                        <button type="button" className={styles.accountCalloutButton} onClick={() => setAuthOpen(true)}>
                            Inicia sesion o crea una cuenta gratis
                        </button>{' '}
                        para verla en <Link to="/mis-alertas">Mis alertas</Link> desde cualquier dispositivo.
                    </p>
                </div>
            )}

            <div className={styles.fieldGrid}>
                <label>
                    <span>Producto, marca o tienda</span>
                    <input
                        type="text"
                        value={keyword}
                        placeholder="PS5, Xiaomi, Druni..."
                        onChange={(event) => setKeyword(event.target.value)}
                    />
                </label>
                <label>
                    <span>Categoria</span>
                    <select value={category} onChange={(event) => setCategory(event.target.value)}>
                        {categories.map((item) => (
                            <option key={item.slug} value={item.slug}>{item.name}</option>
                        ))}
                    </select>
                </label>
                <label>
                    <span>Email opcional</span>
                    <input
                        type="email"
                        value={email}
                        placeholder="Para avisos reales mas adelante"
                        onChange={(event) => setEmail(event.target.value)}
                    />
                </label>
                <label>
                    <span>Precio maximo</span>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={maxPrice}
                        placeholder="Opcional"
                        onChange={(event) => setMaxPrice(event.target.value)}
                    />
                </label>
            </div>
            <button type="submit" className={styles.submitButton} disabled={isSaving}>
                {isSaving ? 'Guardando...' : '🔔 Crear alerta gratis'}
            </button>
            <div className={styles.alertTips} aria-label="Que revisa la alerta">
                <span>Producto o marca</span>
                <span>Categoria</span>
                <span>Precio objetivo</span>
            </div>
            {status && <p className={styles.status} role="status">{status}</p>}

            {accountPrompt && !user && (
                <div className={styles.postSaveAccountPrompt} role="region" aria-label="Guardar la alerta en una cuenta">
                    <strong>¿Quieres conservarla de verdad?</strong>
                    <p>Con una cuenta gratis pasamos esta alerta a tu perfil para verla desde cualquier dispositivo y avisarte por email.</p>
                    <button type="button" className={styles.accountCalloutButton} onClick={() => setAuthOpen(true)}>
                        Crear cuenta gratis
                    </button>
                </div>
            )}

            {Array.isArray(matches) && matches.length > 0 && (
                <div className={styles.matchesBox} aria-label="Ofertas que ya encajan con la alerta">
                    <span className={styles.matchesTitle}>Ya hay ofertas que encajan:</span>
                    <ul className={styles.matchesList}>
                        {matches.slice(0, 4).map((deal) => (
                            <li key={deal.id}>
                                <Link to={`/ofertas/${deal.id}`} className={styles.matchCard}>
                                    {deal.image && <img src={deal.image} alt="" loading="lazy" />}
                                    <span className={styles.matchInfo}>
                                        <span className={styles.matchName}>{deal.name}</span>
                                        <span className={styles.matchMeta}>
                                            {deal.store} · {Number(deal.price || 0).toFixed(2)}€
                                        </span>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <Link to="/mis-alertas" className={styles.inlineLink}>Ver todas mis alertas</Link>
                </div>
            )}

            {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
        </form>
    );
};

function saveLocalAlert(alert) {
    const existingAlerts = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    window.localStorage.setItem(storageKey, JSON.stringify([alert, ...existingAlerts].slice(0, 12)));
}

async function fetchCurrentMatches(alert) {
    const params = new URLSearchParams({
        keyword: alert.keyword,
        category: alert.category
    });

    if (alert.maxPrice) {
        params.set('maxPrice', String(alert.maxPrice));
    }

    const response = await fetch(`/api/alerts/matches?${params.toString()}`);
    if (!response.ok) {
        return null;
    }

    const matches = await response.json();
    return Array.isArray(matches) ? matches : null;
}

function buildStatus(keyword, matches, localOnly, isLoggedIn) {
    if (Array.isArray(matches)) {
        const savedWhere = isLoggedIn ? 'en tu cuenta' : 'en este navegador';
        return `Alerta creada y guardada ${savedWhere} para ${keyword}. Hay ${matches.length} ofertas que encajan ahora.`;
    }

    if (localOnly) {
        return `Alerta guardada en este navegador para ${keyword}.`;
    }

    return `Alerta creada para ${keyword}.`;
}

export default AlertBuilder;

function getAlertPrefill() {
    const params = new URLSearchParams(window.location.search);
    const requestedCategory = params.get('alertCategory') || '';
    const category = categories.some((item) => item.slug === requestedCategory)
        ? requestedCategory
        : (categories[0]?.slug || 'tecnologia');
    const numericPrice = Number(params.get('alertMaxPrice'));

    return {
        keyword: (params.get('alerta') || '').trim().slice(0, 100),
        category,
        maxPrice: Number.isFinite(numericPrice) && numericPrice > 0 ? numericPrice.toFixed(2) : ''
    };
}
